import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { heroSlideService } from '../../services/api';
import DataTable from '../../components/ui/DataTable';
import Spinner from '../../components/ui/Spinner';
import ToastProvider, { useToast } from '../../components/ui/ToastProvider';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Modal from '../../components/ui/Modal';
import ImageUpload from '../../components/admin/ImageUpload';
import { adminTheme } from '../../theme-admin';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${adminTheme.spacing.xl};
  animation: slideIn 0.3s ease-out;
`;

const Title = styled.h2`
  font-family: ${adminTheme.fonts.heading};
  font-size: ${adminTheme.fontSizes['3xl']};
  font-weight: ${adminTheme.fontWeights.bold};
  color: ${adminTheme.colors.gray[900]};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.md};
`;

const AddButton = styled.button`
  padding: ${adminTheme.spacing.md} ${adminTheme.spacing.xl};
  background: linear-gradient(135deg, ${adminTheme.colors.primary[500]} 0%, ${adminTheme.colors.primary[600]} 100%);
  color: white;
  border: none;
  border-radius: ${adminTheme.borderRadius.xl};
  font-weight: ${adminTheme.fontWeights.semibold};
  font-size: ${adminTheme.fontSizes.base};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  box-shadow: ${adminTheme.shadows.md};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${adminTheme.shadows.lg};
  }

  &:active {
    transform: translateY(0);
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${adminTheme.spacing.sm};
  margin-bottom: ${adminTheme.spacing.lg};
`;

const Label = styled.label`
  font-size: ${adminTheme.fontSizes.sm};
  font-weight: ${adminTheme.fontWeights.semibold};
  color: ${adminTheme.colors.gray[700]};
`;

const Input = styled.input`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.base};
  transition: ${adminTheme.transitions.fast};
  font-family: ${adminTheme.fonts.primary};
  color: ${adminTheme.colors.gray[900]};
  background-color: ${adminTheme.colors.background.primary};

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }

  &::placeholder {
    color: ${adminTheme.colors.gray[400]};
  }
`;

const Select = styled.select`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.base};
  transition: ${adminTheme.transitions.fast};
  font-family: ${adminTheme.fonts.primary};
  color: ${adminTheme.colors.gray[900]};
  background-color: ${adminTheme.colors.background.primary};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }

  option {
    padding: ${adminTheme.spacing.sm};
  }
`;

const TextArea = styled.textarea`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.base};
  transition: ${adminTheme.transitions.fast};
  font-family: ${adminTheme.fonts.primary};
  color: ${adminTheme.colors.gray[900]};
  background-color: ${adminTheme.colors.background.primary};
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }

  &::placeholder {
    color: ${adminTheme.colors.gray[400]};
  }
`;

const TableWrapper = styled.div`
  background: ${adminTheme.colors.background.primary};
  border: 1px solid ${adminTheme.colors.gray[200]};
  border-radius: ${adminTheme.borderRadius['2xl']};
  padding: ${adminTheme.spacing.xl};
  box-shadow: ${adminTheme.shadows.md};
  animation: slideUp 0.4s ease-out;
`;

const ActionButton = styled.button`
  padding: ${adminTheme.spacing.sm} ${adminTheme.spacing.md};
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.semibold};
  font-size: ${adminTheme.fontSizes.sm};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  border: 1px solid ${adminTheme.colors.gray[300]};
  background: ${adminTheme.colors.background.primary};
  color: ${adminTheme.colors.gray[700]};

  &:hover {
    background: ${adminTheme.colors.gray[100]};
    transform: translateY(-1px);
  }
`;

const DeleteButton = styled(ActionButton)`
  border-color: ${adminTheme.colors.danger[300]};
  background: ${adminTheme.colors.danger[500]};
  color: white;

  &:hover {
    background: ${adminTheme.colors.danger[600]};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${adminTheme.spacing.sm};
`;

/* Removed unused SaveButton and CancelButton to satisfy CI lint rules */

const ThumbnailImage = styled.img`
  width: 100px;
  height: 60px;
  object-fit: cover;
  border-radius: ${adminTheme.borderRadius.lg};
  border: 2px solid ${adminTheme.colors.gray[200]};
`;

const Badge = styled.span`
  display: inline-block;
  padding: ${adminTheme.spacing.xs} ${adminTheme.spacing.sm};
  border-radius: ${adminTheme.borderRadius.md};
  font-size: ${adminTheme.fontSizes.xs};
  font-weight: ${adminTheme.fontWeights.semibold};
  background: ${props => props.active ? adminTheme.colors.success[100] : adminTheme.colors.gray[200]};
  color: ${props => props.active ? adminTheme.colors.success[700] : adminTheme.colors.gray[600]};
`;

const ToggleSwitch = styled.label`
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.md};
  cursor: pointer;
`;

const ToggleInput = styled.input`
  position: relative;
  width: 50px;
  height: 26px;
  appearance: none;
  background: ${props => props.checked ? adminTheme.colors.success[500] : adminTheme.colors.gray[300]};
  border-radius: 26px;
  transition: ${adminTheme.transitions.fast};
  cursor: pointer;

  &:before {
    content: '';
    position: absolute;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    top: 2px;
    left: ${props => props.checked ? '26px' : '2px'};
    background: white;
    transition: ${adminTheme.transitions.fast};
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${props => props.checked ? adminTheme.colors.success[100] : adminTheme.colors.gray[200]};
  }
`;

const HeroSlidesContent = () => {
  const toast = useToast();
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    subtitle: '',
    image: '',
    buttonText: 'Get Started',
    buttonLink: '/courses',
    order: 0,
    isActive: true,
    showText: true
  });

  const loadSlides = useCallback(async () => {
    try {
      setLoading(true);
      const response = await heroSlideService.getAll({ includeAll: true });
      
      if (response.success) {
        setSlides(response.data);
      } else {
        toast.error('Failed to load hero slides');
      }
    } catch (error) {
  toast.error('Failed to load hero slides');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadSlides();
  }, [loadSlides]);

  const openAddModal = () => {
    setEditForm({
      title: '',
      subtitle: '',
      image: '',
      buttonText: 'Get Started',
      buttonLink: '/courses',
      order: slides.length,
      isActive: true,
      showText: true
    });
    setSelectedSlide(null);
    setShowModal(true);
  };

  const openEditModal = (slide) => {
    setEditForm({
      title: slide.title || '',
      subtitle: slide.subtitle || '',
      image: slide.image || '',
      buttonText: slide.buttonText || 'Get Started',
      buttonLink: slide.buttonLink || '/courses',
      order: slide.order || 0,
      isActive: slide.isActive !== undefined ? slide.isActive : true,
      showText: slide.showText !== undefined ? slide.showText : true
    });
    setSelectedSlide(slide);
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (!editForm.title || !editForm.subtitle || !editForm.image) {
        toast.error('Please fill in all required fields');
        return;
      }

      const slideData = {
        title: editForm.title,
        subtitle: editForm.subtitle,
        image: editForm.image,
        buttonText: editForm.buttonText,
        buttonLink: editForm.buttonLink,
        order: parseInt(editForm.order) || 0,
        isActive: editForm.isActive,
        showText: editForm.showText !== undefined ? editForm.showText : true
      };

      let response;
      if (selectedSlide) {
        response = await heroSlideService.update(selectedSlide._id, slideData);
      } else {
        response = await heroSlideService.create(slideData);
      }

      if (response.success) {
        toast.success(selectedSlide ? 'Slide updated successfully' : 'Slide created successfully');
        setShowModal(false);
        loadSlides();
      } else {
        toast.error(response.message || 'Failed to save slide');
      }
    } catch (error) {
      toast.error('Failed to save slide');
    }
  };

  const handleDelete = async () => {
    try {
      const response = await heroSlideService.delete(selectedSlide._id);
      
      if (response.success) {
        toast.success('Slide deleted successfully');
        setShowDeleteDialog(false);
        loadSlides();
      } else {
        toast.error('Failed to delete slide');
      }
    } catch (error) {
      toast.error('Failed to delete slide');
    }
  };

  const openDeleteDialog = (slide) => {
    setSelectedSlide(slide);
    setShowDeleteDialog(true);
  };

  const columns = [
    {
      key: 'image',
      label: 'Image',
      render: (slide) => (
        <ThumbnailImage src={slide.image} alt={slide.title} />
      )
    },
    {
      key: 'title',
      label: 'Title'
    },
    {
      key: 'subtitle',
      label: 'Subtitle',
      render: (slide) => (
        <span style={{ maxWidth: '300px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {slide.subtitle}
        </span>
      )
    },
    {
      key: 'order',
      label: 'Order',
      render: (slide) => (
        <Badge>{slide.order}</Badge>
      )
    },
    {
      key: 'isActive',
      label: 'Status',
      render: (slide) => (
        <Badge active={slide.isActive}>
          {slide.isActive ? 'Active' : 'Inactive'}
        </Badge>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (slide) => (
        <ButtonGroup>
          <ActionButton onClick={() => openEditModal(slide)}>
            Edit
          </ActionButton>
          <DeleteButton onClick={() => openDeleteDialog(slide)}>
            Delete
          </DeleteButton>
        </ButtonGroup>
      )
    }
  ];

  if (loading) {
    return <Spinner />;
  }

  return (
    <>
      <PageHeader>
        <Title>
          🎭 Hero Slides
        </Title>
        <AddButton onClick={openAddModal}>
          + Add New Slide
        </AddButton>
      </PageHeader>

      <TableWrapper>
        <DataTable
          columns={columns}
          rows={slides}
          emptyMessage="No hero slides found. Add your first slide!"
        />
      </TableWrapper>

      <Modal
        open={showModal}
        title={selectedSlide ? 'Edit Hero Slide' : 'Add New Hero Slide'}
        onCancel={() => setShowModal(false)}
        onSave={handleSave}
        saveLabel={selectedSlide ? 'Update Slide' : 'Create Slide'}
      >
        <FormGroup>
          <Label>Title *</Label>
          <Input
            type="text"
            placeholder="Enter slide title"
            value={editForm.title}
            onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
          />
        </FormGroup>

        <FormGroup>
          <Label>Subtitle *</Label>
          <TextArea
            placeholder="Enter slide subtitle"
            value={editForm.subtitle}
            onChange={(e) => setEditForm({ ...editForm, subtitle: e.target.value })}
          />
        </FormGroup>

        <FormGroup>
          <Label>Slide Image *</Label>
          <ImageUpload
            value={editForm.image}
            onChange={(imageUrl) => setEditForm({ ...editForm, image: imageUrl })}
            label="Upload Slide Image"
          />
        </FormGroup>

        <FormGroup>
          <Label>Button Text</Label>
          <Input
            type="text"
            placeholder="Get Started"
            value={editForm.buttonText}
            onChange={(e) => setEditForm({ ...editForm, buttonText: e.target.value })}
          />
        </FormGroup>

        <FormGroup>
          <Label>Button Link</Label>
          <Select
            value={editForm.buttonLink}
            onChange={(e) => setEditForm({ ...editForm, buttonLink: e.target.value })}
          >
            <option value="/courses">All Courses</option>
            <option value="/about">About Us</option>
            <option value="/contact">Contact Us</option>
            <option value="/blog">Blog</option>
            <option value="/">Home</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <ToggleSwitch>
            <ToggleInput
              type="checkbox"
              checked={editForm.showText}
              onChange={(e) => setEditForm({ ...editForm, showText: e.target.checked })}
            />
            <Label>Show text on slide (if unchecked, only image will be displayed)</Label>
          </ToggleSwitch>
        </FormGroup>

        <FormGroup>
          <Label>Order (Lower numbers appear first)</Label>
          <Input
            type="number"
            placeholder="0"
            value={editForm.order}
            onChange={(e) => setEditForm({ ...editForm, order: e.target.value })}
          />
        </FormGroup>

        <FormGroup>
          <ToggleSwitch>
            <ToggleInput
              type="checkbox"
              checked={editForm.isActive}
              onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
            />
            <Label>Active (Show on homepage)</Label>
          </ToggleSwitch>
        </FormGroup>
      </Modal>

      <ConfirmDialog
        open={showDeleteDialog}
        title="Delete Hero Slide"
        description={`Are you sure you want to delete "${selectedSlide?.title}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

const HeroSlides = () => {
  return (
    <ToastProvider>
      <HeroSlidesContent />
    </ToastProvider>
  );
};

export default HeroSlides;
